import React, { useState } from 'react';
import { Search, MapPin, Sun, Moon } from 'lucide-react';
import { Button } from "./Component/ui/button";
import { Input } from "./Component/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./Component/ui/card";

export default function Component() {
  const [city, setCity] = useState('');
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const fetchWeather = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const geoResponse = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(city)}&count=1&language=en&format=json`
      );
      const geoData = await geoResponse.json();

      if (!geoData.results || geoData.results.length === 0) {
        throw new Error('City not found');
      }

      const { latitude, longitude, name, country } = geoData.results[0];
      await fetchWeatherData(latitude, longitude, name, country);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchWeatherData = async (latitude, longitude, name, country) => {
    try {
      const weatherResponse = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current_weather=true&hourly=temperature_2m,relativehumidity_2m,windspeed_10m`
      );
      const weatherData = await weatherResponse.json();

      setWeather({
        name,
        country,
        current: weatherData.current_weather,
        hourly: weatherData.hourly
      });
    } catch (err) {
      setError('Failed to fetch weather data');
    } finally {
      setLoading(false);
    }
  };

  const handleCurrentLocation = () => {
    setLoading(true);
    setError(null);

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
            );
            const data = await response.json();
            await fetchWeatherData(latitude, longitude, data.city, data.countryName);
          } catch (err) {
            setError('Failed to get location name');
            setLoading(false);
          }
        },
        (err) => {
          setError('Failed to get current location');
          setLoading(false);
        }
      );
    } else {
      setError('Geolocation is not supported by your browser');
      setLoading(false);
    }
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <div className={`${isDarkMode ? 'dark' : ''}`}>
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gradient-to-b from-blue-100 to-blue-300 text-gray-900'} flex items-center justify-center p-4`}>
        <Card isDarkMode={isDarkMode} className="w-full max-w-md shadow-lg rounded-lg overflow-hidden">
          <CardHeader isDarkMode={isDarkMode}>
            <CardTitle isDarkMode={isDarkMode} className="text-2xl font-bold text-center">
              Weather App
            </CardTitle>
            <CardDescription isDarkMode={isDarkMode} className="text-center">
              Enter a city name or use your current location
            </CardDescription>
          </CardHeader>
          <CardContent isDarkMode={isDarkMode}>
            <div className="flex justify-end mb-4">
            <Button 
  onClick={toggleDarkMode} 
  className={`${
    isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
  } text-white font-bold py-2 px-4 rounded-full flex items-center justify-center`}
>
  {isDarkMode ? <Sun className="h-5 w-5 text-yellow-400" /> : <Moon className="h-5 w-5 text-gray-800" />}
</Button>
            </div>
            <form onSubmit={fetchWeather} className="flex gap-2 mb-4">
              <Input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Enter city name"
                isDarkMode={isDarkMode}
                className="placeholder:text-center"
              />
              <Button type="submit" disabled={loading} className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded">
                {loading ? 'Searching...' : <Search className="h-4 w-4" />}
              </Button>
            </form>
            <Button 
              onClick={handleCurrentLocation} 
              disabled={loading}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded flex items-center justify-center"
            >
              <MapPin className="h-4 w-4 mr-2" />
              {loading ? 'Locating...' : 'Use Current Location'}
            </Button>

            {error && (
              <p className="text-red-500 text-center mt-4">{error}</p>
            )}

            {weather && (
              <div className="text-center mt-4">
                <h2 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {weather.name}, {weather.country}
                </h2>
                <p className={`text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {Math.round(weather.current.temperature)}°C
                </p>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <div>
                    <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Wind Speed</p>
                    <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>{weather.current.windspeed} km/h</p>
                  </div>
                  <div>
                    <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Humidity</p>
                    <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>{weather.hourly.relativehumidity_2m[0]}%</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}